import type { ReactElement } from 'react'
import { CONTACT_TYPE_OPTIONS, type ChoiceOption, type FieldDef, type FieldType } from '../schema/intakeSchema'
import { ExpandableTextarea } from './ExpandableTextarea'
import { HelpTooltip } from './HelpTooltip'
import { PriorityBadge } from './PriorityBadge'
import { safeHttpUrl } from '../utils/safeUrl'

type Value = unknown
type SetValue = (v: Value) => void

/** Coerce a captured value to a string[] without crashing. Imports / seed data
 *  occasionally arrive as a single string ("Sales, Marketing") where the schema
 *  expects an array; this normalizes both shapes. */
function toStringArray(v: Value, sep: string | RegExp = ','): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string')
  if (typeof v === 'string')
    return v
      .split(sep)
      .map((s) => s.trim())
      .filter(Boolean)
  return []
}

/** Coerce a captured value to an array of objects without crashing. */
function toObjectArray<T extends object>(v: Value): T[] {
  if (Array.isArray(v)) return v.filter((x): x is T => typeof x === 'object' && x !== null)
  return []
}

interface Props {
  field: FieldDef
  value: Value
  onChange: SetValue
  meta?:
    | { source?: string; url?: string | null | undefined; actor?: string | null | undefined }
    | undefined
}

export function FieldRenderer({ field, value, onChange, meta }: Props) {
  // One muted helper line: who last touched the field (FieldMeta.actor,
  // stamped server-side) and/or where a prefill came from.
  const sourceHref = safeHttpUrl(meta?.url)
  const helper =
    meta?.url || meta?.actor ? (
      <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
        {meta.actor && <>Last edited by {meta.actor}</>}
        {meta.actor && meta.url && ' · '}
        {meta.url && (
          <>
            Prefilled from {meta.source}{' '}
            {sourceHref ? (
              <a href={sourceHref} target="_blank" rel="noopener noreferrer">
                (source)
              </a>
            ) : (
              <>({meta.url})</>
            )}
          </>
        )}
      </div>
    ) : null

  return (
    <div data-testid={`field-${field.key}`} style={{ marginBottom: '1.6rem' }}>
      <label style={{ display: 'flex', alignItems: 'center', fontWeight: 600, marginBottom: '0.45rem' }}>
        <span>{field.label}</span>
        <PriorityBadge priority={field.priority} />
        {(field.hint || field.help) && (
          <HelpTooltip
            content={field.hint ?? ''}
            help={field.help}
            label={field.label}
            ariaLabel={`Help for ${field.label}`}
          />
        )}
      </label>
      <FieldBody field={field} value={value} onChange={onChange} />
      {helper}
    </div>
  )
}

/** Props every kind renderer receives; `type` is narrowed to that kind's shape. */
interface KindProps<K extends FieldType['kind']> {
  field: FieldDef
  type: Extract<FieldType, { kind: K }>
  value: Value
  onChange: SetValue
}

type KindRenderers = { [K in FieldType['kind']]: (props: KindProps<K>) => ReactElement }

/** One renderer per schema field kind. Adding a kind to `FieldType` without an
 *  entry here is a compile error. */
const KIND_RENDERERS: KindRenderers = {
  text: ({ type, value, onChange }) => (
    <input
      type="text"
      value={(value as string) ?? ''}
      placeholder={type.placeholder}
      spellCheck
      onChange={(e) => onChange(e.target.value)}
    />
  ),
  textarea: ({ field, type, value, onChange }) => (
    <ExpandableTextarea
      label={field.label}
      value={(value as string) ?? ''}
      // The schema's `rows` are semantic sizes (2 = short, 3 = paragraph);
      // doubled at render time — the literal heights were cramped to type in.
      rows={(type.rows ?? 3) * 2}
      placeholder={type.placeholder}
      onChange={(v) => onChange(v)}
    />
  ),
  number: ({ type, value, onChange }) => (
    <input
      type="number"
      value={(value as number) ?? ''}
      placeholder={type.placeholder}
      min={type.min}
      max={type.max}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    />
  ),
  radio: ({ field, type, value, onChange }) => (
    <RadioGroup name={field.key} options={type.options} value={value as string} onChange={onChange} />
  ),
  multiselect: ({ type, value, onChange }) => (
    <MultiSelect
      options={type.options}
      allowOther={!!type.allowOther}
      value={toStringArray(value, ',')}
      onChange={onChange}
    />
  ),
  select: ({ field, type, value, onChange }) => (
    <select
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      data-testid={`select-${field.key}`}
    >
      <option value="">{type.placeholder ?? '—'}</option>
      {type.options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  ),
  list: ({ type, value, onChange }) => (
    <ListInput value={toStringArray(value, /\r?\n/)} placeholder={type.placeholder} onChange={onChange} />
  ),
  'eligibility-grid': ({ type, value, onChange }) => (
    <EligibilityGrid rows={type.rows} value={value as EligibilityValue} onChange={onChange} />
  ),
  'use-cases-table': ({ value, onChange }) => (
    <UseCasesTable value={toObjectArray<UseCaseRow>(value)} onChange={onChange} />
  ),
  'qa-list': ({ value, onChange }) => <QAList value={toObjectArray<QARow>(value)} onChange={onChange} />,
  'contacts-table': ({ value, onChange }) => (
    <ContactsTable value={toObjectArray<ContactRow>(value)} onChange={onChange} />
  ),
}

function FieldBody({ field, value, onChange }: { field: FieldDef; value: Value; onChange: SetValue }) {
  // Stored schemas / imports can carry a kind this build doesn't know.
  const kind = field.type.kind
  if (!Object.hasOwn(KIND_RENDERERS, kind)) return <div>Unknown field type</div>
  const Render = KIND_RENDERERS[kind] as (props: KindProps<typeof kind>) => ReactElement
  return <Render field={field} type={field.type} value={value} onChange={onChange} />
}

/** One row of the company contact roster (company_profile.contacts). */
export interface ContactRow {
  name?: string | undefined
  title?: string | undefined
  contact_type?: string | undefined
  email?: string | undefined
  phone?: string | undefined
}

function ContactsTable({
  value,
  onChange,
}: {
  value?: ContactRow[] | undefined
  onChange: SetValue
}) {
  const rows = value ?? []
  const update = (i: number, patch: Partial<ContactRow>) => {
    const next = rows.slice()
    next[i] = { ...next[i], ...patch }
    onChange(next)
  }
  const addRow = () => onChange([...rows, {}])
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i))

  const cellLabel = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', marginBottom: '0.2rem' } as const

  return (
    <div>
      {rows.map((r, i) => (
        // Two-line card per contact: who they are, then how to reach them.
        <div
          key={i}
          data-testid={`contact-card-${i}`}
          style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 0.75rem', marginBottom: '0.5rem' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Contact {i + 1}</span>
            <button type="button" onClick={() => remove(i)} aria-label="remove contact" data-testid={`contact-remove-${i}`} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: '1rem', lineHeight: 1, padding: 0 }}>✕</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <label>
              <span style={cellLabel}>Name</span>
              <input type="text" value={r.name ?? ''} spellCheck data-testid={`contact-name-${i}`} onChange={(e) => update(i, { name: e.target.value })} />
            </label>
            <label>
              <span style={cellLabel}>Title</span>
              <input type="text" value={r.title ?? ''} spellCheck placeholder="CEO, VP Eng…" onChange={(e) => update(i, { title: e.target.value })} />
            </label>
            <label>
              <span style={cellLabel}>Type</span>
              <select value={r.contact_type ?? ''} data-testid={`contact-type-${i}`} onChange={(e) => update(i, { contact_type: e.target.value || undefined })}>
                <option value="">—</option>
                {CONTACT_TYPE_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr', gap: '0.5rem' }}>
            <label>
              <span style={cellLabel}>Email</span>
              <input type="email" value={r.email ?? ''} data-testid={`contact-email-${i}`} onChange={(e) => update(i, { email: e.target.value })} />
            </label>
            <label>
              <span style={cellLabel}>Phone</span>
              <input type="tel" value={r.phone ?? ''} onChange={(e) => update(i, { phone: e.target.value })} />
            </label>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        style={{ marginTop: '0.5rem' }}
        data-testid="add-contact-row"
      >
        + Add contact
      </button>
    </div>
  )
}

function RadioGroup({ name, options, value, onChange }: { name: string; options: ChoiceOption[]; value?: string | undefined; onChange: SetValue }) {
  // Long option lists (Industry has 12) stack into a wall of radios; lay them
  // out in a responsive multi-column grid so they read at a glance. Short lists
  // keep the single column. (DESIGN.md §4 — Intake.)
  const many = options.length > 6
  return (
    <div className={`radio-group${many ? ' radio-group--grid' : ''}`}>
      {options.map((o) => {
        const checked = value === o.value
        return (
          <label key={o.value} className={`radio-option${checked ? ' radio-option--checked' : ''}`}>
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={checked}
              onChange={() => onChange(o.value)}
            />
            <span>{o.label}</span>
          </label>
        )
      })}
    </div>
  )
}

function MultiSelect({ options, value, allowOther, onChange }: { options: ChoiceOption[]; value?: string[] | undefined; allowOther: boolean; onChange: SetValue }) {
  const selected = new Set(value ?? [])
  const known = new Set(options.map((o) => o.value))
  const otherText = (value ?? []).filter((v) => !known.has(v)).join(', ')

  const toggle = (v: string) => {
    const next = new Set(selected)
    if (next.has(v)) next.delete(v)
    else next.add(v)
    onChange(Array.from(next))
  }

  // During typing we preserve the raw split — including a partial trailing
  // entry the user is still typing ("Foo, Bar, Bazz<cursor>"). Trimming and
  // empty-filter only run on blur so the user can type spaces and commas
  // mid-word without the controlled re-render resetting them.
  const updateOtherRaw = (raw: string) => {
    const knownSelected = (value ?? []).filter((v) => known.has(v))
    const extras = raw.split(',')
    onChange([...knownSelected, ...extras])
  }
  const finalizeOther = (raw: string) => {
    const knownSelected = (value ?? []).filter((v) => known.has(v))
    const extras = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    onChange([...knownSelected, ...extras])
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.25rem' }}>
        {options.map((o) => (
          <label key={o.value} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <input type="checkbox" checked={selected.has(o.value)} onChange={() => toggle(o.value)} />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
      {allowOther && (
        <input
          type="text"
          placeholder="Other (comma-separated)"
          value={otherText}
          spellCheck
          onChange={(e) => updateOtherRaw(e.target.value)}
          onBlur={(e) => finalizeOther(e.target.value)}
          style={{ marginTop: '0.5rem' }}
        />
      )}
    </div>
  )
}

function ListInput({ value, placeholder, onChange }: { value?: string[] | undefined; placeholder?: string | undefined; onChange: SetValue }) {
  const text = (value ?? []).join('\n')
  const commit = (raw: string) =>
    onChange(
      raw.split('\n').map((l) => l.trim()).filter(Boolean),
    )
  // While the user is typing we preserve every keystroke verbatim — empty
  // lines and trailing whitespace included. Trimming/filtering during
  // onChange ate trailing spaces (the controlled re-render reset the value
  // before the user could continue typing). We only canonicalize on blur.
  return (
    <ExpandableTextarea
      label="List"
      value={text}
      placeholder={placeholder ?? 'One per line'}
      rows={Math.max(3, (value?.length ?? 0) + 1)}
      onChange={(v) => onChange(v.split('\n'))}
      onBlur={() => commit(text)}
    />
  )
}

type EligibilityRow = { result?: 'Pass' | 'Fail' | undefined; notes?: string | undefined }
type EligibilityValue = Record<string, EligibilityRow>

function EligibilityGrid({ rows, value, onChange }: { rows: ChoiceOption[]; value?: EligibilityValue | undefined; onChange: SetValue }) {
  const v: EligibilityValue = value ?? {}
  const update = (key: string, patch: Partial<EligibilityRow>) => {
    onChange({ ...v, [key]: { ...v[key], ...patch } })
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--border)' }}>
          <th style={{ textAlign: 'left', padding: '0.4rem' }}>Check</th>
          <th style={{ width: '4rem', padding: '0.4rem', textAlign: 'center' }}>Pass</th>
          <th style={{ textAlign: 'left', padding: '0.4rem' }}>Notes</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const row = v[r.value] ?? {}
          return (
            <tr key={r.value} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '0.4rem', verticalAlign: 'top' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span>{r.label}</span>
                  {(r.hint || r.help) && (
                    <HelpTooltip
                      content={r.hint ?? ''}
                      help={r.help}
                      label={r.label}
                      ariaLabel={`Help for ${r.label}`}
                    />
                  )}
                </div>
              </td>
              <td style={{ padding: '0.4rem', verticalAlign: 'top', textAlign: 'center' }}>
                {/* Checked = this rule passes. Unchecked = not satisfied (a
                    disqualifier); downstream only ever counts result === 'Pass'. */}
                <input
                  type="checkbox"
                  checked={row.result === 'Pass'}
                  onChange={(e) => update(r.value, { result: e.target.checked ? 'Pass' : undefined })}
                  data-testid={`elig-pass-${r.value}`}
                  aria-label={`${r.label} — passes`}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
              </td>
              <td style={{ padding: '0.4rem' }}>
                <input
                  type="text"
                  value={row.notes ?? ''}
                  spellCheck
                  onChange={(e) => update(r.value, { notes: e.target.value })}
                />
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

interface UseCaseRow {
  name?: string | undefined
  description?: string | undefined
  type?: 'New' | 'Modernization' | 'Migration' | undefined
  impact?: 'H' | 'M' | 'L' | undefined
}

interface QARow {
  question?: string | undefined
  answer?: string | undefined
}

function QAList({ value, onChange }: { value?: QARow[] | undefined; onChange: SetValue }) {
  const rows = value ?? []
  const update = (i: number, patch: Partial<QARow>) => {
    const next = rows.slice()
    next[i] = { ...next[i], ...patch }
    onChange(next)
  }
  const addRow = () => onChange([...rows, {}])
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i))

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ textAlign: 'left', padding: '0.4rem', width: '40%' }}>Question</th>
            <th style={{ textAlign: 'left', padding: '0.4rem' }}>Answer</th>
            <th style={{ width: '3rem' }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '0.3rem', verticalAlign: 'top' }}>
                <ExpandableTextarea
                  label={`Question ${i + 1}`}
                  value={r.question ?? ''}
                  rows={2}
                  onChange={(v) => update(i, { question: v })}
                  testId={`qa-question-${i}`}
                />
              </td>
              <td style={{ padding: '0.3rem', verticalAlign: 'top' }}>
                <ExpandableTextarea
                  label={`Answer ${i + 1}`}
                  value={r.answer ?? ''}
                  rows={2}
                  onChange={(v) => update(i, { answer: v })}
                  testId={`qa-answer-${i}`}
                />
              </td>
              <td style={{ padding: '0.3rem', textAlign: 'center', verticalAlign: 'top' }}>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label="remove Q&A row"
                  data-testid={`qa-remove-${i}`}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={addRow}
        style={{ marginTop: '0.5rem' }}
        data-testid="add-qa-row"
      >
        + Add Q&A
      </button>
    </div>
  )
}

function UseCasesTable({
  value,
  onChange,
}: {
  value?: UseCaseRow[] | undefined
  onChange: SetValue
}) {
  const rows = value ?? []
  const update = (i: number, patch: Partial<UseCaseRow>) => {
    const next = rows.slice()
    next[i] = { ...next[i], ...patch }
    onChange(next)
  }
  const addRow = () => onChange([...rows, {}])
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i))

  return (
    <div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border)' }}>
            <th style={{ textAlign: 'left', padding: '0.4rem' }}>Name</th>
            <th style={{ textAlign: 'left', padding: '0.4rem' }}>Description</th>
            <th style={{ width: '9rem', padding: '0.4rem' }}>Type</th>
            <th style={{ width: '6rem', padding: '0.4rem' }}>Impact</th>
            <th style={{ width: '3rem' }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '0.3rem' }}>
                <input type="text" value={r.name ?? ''} spellCheck onChange={(e) => update(i, { name: e.target.value })} />
              </td>
              <td style={{ padding: '0.3rem' }}>
                <input type="text" value={r.description ?? ''} spellCheck onChange={(e) => update(i, { description: e.target.value })} />
              </td>
              <td style={{ padding: '0.3rem' }}>
                <select value={r.type ?? ''} onChange={(e) => update(i, { type: (e.target.value || undefined) as UseCaseRow['type'] })}>
                  <option value="">—</option>
                  <option value="New">New</option>
                  <option value="Modernization">Modernization</option>
                  <option value="Migration">Migration</option>
                </select>
              </td>
              <td style={{ padding: '0.3rem' }}>
                <select value={r.impact ?? ''} onChange={(e) => update(i, { impact: (e.target.value || undefined) as UseCaseRow['impact'] })}>
                  <option value="">—</option>
                  <option value="H">High</option>
                  <option value="M">Medium</option>
                  <option value="L">Low</option>
                </select>
              </td>
              <td style={{ padding: '0.3rem', textAlign: 'center' }}>
                <button type="button" onClick={() => remove(i)} aria-label="remove row">✕</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={addRow}
        style={{ marginTop: '0.5rem' }}
        data-testid="add-use-case-row"
      >
        + Add row
      </button>
    </div>
  )
}
